import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, UserPlus, Mail, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Autocomplete search for existing users + invite by email fallback
export const TeamInviteBox = ({ eventId, teamId, token, onInvited, canInvite = true }) => {
  const [mode, setMode] = useState('add');
  const adding = mode === 'add' || !canInvite;
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const timer = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q.trim());

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/api/event-teams/${eventId}/user-search?q=${encodeURIComponent(q.trim())}`, { headers });
        setResults(res.data.users || []);
      } catch (err) {
        if (err.response?.status === 429) toast.error('Slow down — too many searches');
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const invite = async (payload) => {
    setSending(true);
    try {
      const path = adding ? 'members' : 'invites';
      const res = await axios.post(`${API}/api/event-teams/teams/${teamId}/${path}`, payload, { headers });
      if (adding) toast.success(`${res.data.message} — they've been emailed a login/register link`);
      else toast.success(res.data.existing_user ? 'Invitation sent' : 'Invitation emailed — they can sign up and join');
      setQ(''); setResults([]);
      onInvited();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send invite'); }
    finally { setSending(false); }
  };

  return (
    <div className="relative" data-testid="team-invite-box">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <label className="block text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em]">Add teammates</label>
        {canInvite && (
          <div className="flex bg-[#F4F4F5] rounded p-0.5" data-testid="team-add-mode-toggle">
            {[['add', 'Add now'], ['invite', 'Send invite']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setMode(v)} data-testid={`team-add-mode-${v}`}
                className={`px-2.5 py-1 rounded text-[11px] font-semibold ${mode === v ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#71717A]'}`}>{l}</button>
            ))}
          </div>
        )}
      </div>
      <p className="text-[11px] text-[#71717A] mb-2">{adding ? 'Added members get a spot right away (provisional until they log in) — no acceptance needed, you can pay for them immediately.' : 'They join only after accepting the invitation.'}</p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, username or enter an email"
            className="w-full pl-9 pr-3 py-2.5 border border-[#E4E4E7] rounded text-sm focus:outline-none focus:border-[#002FA7]"
            data-testid="team-invite-search-input" />
          {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#A1A1AA]" />}
        </div>
        {isEmail && (
          <button onClick={() => invite({ email: q.trim() })} disabled={sending}
            className="inline-flex items-center gap-1.5 px-4 bg-[#0A0A0A] hover:bg-[#27272A] text-white rounded text-sm font-semibold disabled:opacity-50 transition-colors"
            data-testid="team-invite-email-btn">
            <Mail size={14} /> {adding ? 'Add' : 'Invite'}
          </button>
        )}
      </div>
      {results.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border border-[#E4E4E7] rounded-lg shadow-lg overflow-hidden" data-testid="team-invite-results">
          {results.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-[#F4F4F5]">
              <div className="flex items-center gap-2.5 min-w-0">
                {u.avatar_url
                  ? <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                  : <div className="w-8 h-8 rounded-full bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center text-xs font-bold">{(u.full_name || '?')[0]}</div>}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{u.full_name || u.username}</p>
                  <p className="text-xs text-[#A1A1AA] truncate">{u.email_masked}{u.college ? ` · ${u.college}` : ''}</p>
                </div>
              </div>
              {u.in_team ? (
                <span className="text-[11px] font-semibold text-[#A1A1AA] uppercase">In a team</span>
              ) : (
                <button onClick={() => invite({ user_id: u.id })} disabled={sending}
                  className="inline-flex items-center gap-1 text-xs font-bold text-[#002FA7] hover:underline disabled:opacity-50"
                  data-testid={`team-invite-user-${u.id}`}>
                  <UserPlus size={13} /> {adding ? 'Add' : 'Invite'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
